import React, { useState, useEffect } from 'react';
import { API_ROUTES } from '../constants/routes';
import ProjectInformationModal from '../components/ProjectInformationModal';

export default function Projects() {
  const [projects, setProjects] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);

  useEffect(() => {
    fetchProjects();
  }, []);

  // Filter projects based on search term
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredProjects(projects);
    } else {
      const filtered = projects.filter(project =>
        project.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredProjects(filtered);
    }
  }, [projects, searchTerm]);

  const fetchProjects = async () => {
    try {
      setLoading(true);
      const response = await fetch(API_ROUTES.Project.ALL);
      if (!response.ok) {
        throw new Error('Failed to fetch projects');
      }
      const data = await response.json();
      setProjects(data.data || []);
      setFilteredProjects(data.data || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getProjectImage = (project) => {
    if (project?.image) return project.image;              // single image
    if (Array.isArray(project?.images) && project.images.length > 0) {
      return project.images[0];                             // first image
    }
    return null;
  };


  // Helper function to get status badge color
  const getStatusBadgeColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed':
        return 'bg-green-600';
      case 'inprogress':
        return 'bg-[#0258DA]';
      case 'planning':
        return 'bg-yellow-500';
      case 'cancelled':
        return 'bg-red-500';
      default:
        return 'bg-gray-500';
    }
  };

  // Helper function to get background color based on status
  const getBackgroundColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed':
        return 'bg-green-100';
      case 'inprogress':
        return 'bg-blue-100';
      case 'planning':
        return 'bg-yellow-100';
      case 'cancelled':
        return 'bg-red-100';
      default:
        return 'bg-gray-100';
    }
  };

  // Helper function to get icon color based on status
  const getIconColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed':
        return 'text-green-600';
      case 'inprogress':
        return 'text-[#0258DA]';
      case 'planning':
        return 'text-yellow-600';
      case 'cancelled':
        return 'text-red-600';
      default:
        return 'text-gray-600';
    }
  };

  // Helper function to get client initials
  const getClientInitials = (client) => {
    if (!client) return 'NA';
    return client
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  // Helper function to parse services into array
  const parseServices = (services) => {
    if (!services) return [];
    return services.split(',').map(service => service.trim());
  };

  // Fixed team member initials for consistent display
  const teamInitials = ['MK', 'JD', 'AL'];

  // Handle search input change
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  // Clear search
  const clearSearch = () => {
    setSearchTerm('');
  };

  // Handle modal open
  const handleOpenModal = (project) => {
    setSelectedProject(project);
    setModalOpen(true);
  };

  // Handle modal close
  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedProject(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0258DA] mx-auto mb-4"></div>
          <p className="text-gray-600">Loading projects...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Error Loading Projects</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={fetchProjects}
            className="bg-[#0258DA] text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12 mt-[72px]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-[#0258DA] mb-4">Our Projects</h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Explore our portfolio of innovative student projects and campus solutions that showcase
            our interest in various industries and technologies.
          </p>
        </div>

        {/* Search Bar */}
        <div className="mb-8">
          <div className="relative max-w-md mx-auto">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg
                className="h-5 w-5 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </div>
            <input
              type="text"
              placeholder="Search projects by name..."
              value={searchTerm}
              onChange={handleSearchChange}
              className="block w-full pl-10 pr-10 py-3 border border-gray-300 rounded-lg leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-[#0258DA] focus:border-[#0258DA]"
            />
            {searchTerm && (
              <button
                onClick={clearSearch}
                className="absolute inset-y-0 right-0 pr-3 flex items-center hover:text-gray-600"
              >
                <svg
                  className="h-5 w-5 text-gray-400 hover:text-gray-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            )}
          </div>
        </div>

        {/* Projects Grid */}
        {filteredProjects.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-gray-400 text-6xl mb-4">📁</div>
            <h3 className="text-2xl font-semibold text-gray-600 mb-2">
              {searchTerm ? "No Projects Found" : "No Projects Available"}
            </h3>
            <p className="text-gray-500">
              {searchTerm
                ? `No projects match "${searchTerm}". Try a different search term.`
                : "We're working on some exciting projects. Check back soon!"}
            </p>
            {searchTerm && (
              <button
                onClick={clearSearch}
                className="mt-4 text-[#0258DA] hover:underline"
              >
                Clear search
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredProjects.map(project => (
              <div
                key={project._id}
                className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-300"
              >
                <div className="h-48 relative overflow-hidden">
                  {getProjectImage(project) ? (
                    <>
                      <img
                        src={getProjectImage(project)}
                        alt={project.name}
                        className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
                      />
                      {/* subtle overlay */}
                      <div className="absolute inset-0 bg-black/20"></div>
                    </>
                  ) : (
                    <div className={`w-full h-full ${getBackgroundColor(project.status)} relative`}>
                      <div
                        className={`absolute inset-0 flex items-center justify-center ${getIconColor(project.status)} opacity-20`}
                      >
                        <svg
                          className="w-24 h-24"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path d="M10 3.5a1.5 1.5 0 013 0V4..." />
                        </svg>
                      </div>
                    </div>
                  )}

                  <span
                    className={`absolute top-4 right-4 ${getStatusBadgeColor(project.status)} text-white text-xs font-bold px-3 py-1 rounded-full z-10`}
                  >
                    {project.status}
                  </span>
                </div>

                <div className="p-6">
                  <h3 className="text-xl font-bold mb-2 text-[#0258DA]">{project.name}</h3>
                  <p className="text-[#465362] mb-4 text-sm leading-relaxed">{project.desc}</p>

                  {/* Client and Timeline */}
                  <div className="mb-4">
                    <div className="flex items-center justify-between text-sm text-gray-600 mb-2">
                      <span className="font-medium">Client:</span>
                      <span>{project.client}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm text-gray-600">
                      <span className="font-medium">Timeline:</span>
                      <span>{project.timeline}</span>
                    </div>
                  </div>

                  {/* Services Tags */}
                  <div className="flex flex-wrap gap-2 mb-6">
                    {parseServices(project.services).map((service, index) => (
                      <span
                        key={index}
                        className="bg-blue-100 text-[#0258DA] text-xs font-medium px-3 py-1 rounded-full"
                      >
                        {service}
                      </span>
                    ))}
                  </div>

                  {/* Team and View Details */}
                  <div className="flex justify-between items-center">
                    {/*<div className="flex items-center">*/}
                    {/*  <div className="w-8 h-8 rounded-full bg-[#0258DA] flex items-center justify-center text-white font-bold text-xs">*/}
                    {/*    {getClientInitials(project.client)}*/}
                    {/*  </div>*/}
                    {/*  {teamInitials.map((initial, index) => (*/}
                    {/*    <div key={index} className={`w-8 h-8 rounded-full ${index === 0 ? 'bg-blue-500' : index === 1 ? 'bg-blue-400' : 'bg-blue-300'} flex items-center justify-center text-white font-bold text-xs -ml-2`}>*/}
                    {/*      {initial}*/}
                    {/*    </div>*/}
                    {/*  ))}*/}
                    {/*</div>*/}
                    <button
                      onClick={() => handleOpenModal(project)}
                      className="text-[#0258DA] font-medium hover:underline text-sm cursor-pointer"
                    >
                      Learn More →
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Stats Section */}
        {projects.length > 0 && (
          <div className="mt-16 bg-white rounded-lg shadow-md p-8">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-8 text-center">
              <div>
                <div className="text-3xl font-bold text-[#0258DA] mb-2">
                  {searchTerm ? filteredProjects.length : projects.length}
                </div>
                <div className="text-gray-600">
                  {searchTerm ? "Filtered Projects" : "Total Projects"}
                </div>
              </div>
              <div>
                <div className="text-3xl font-bold text-green-600 mb-2">
                  {
                    (searchTerm ? filteredProjects : projects).filter(
                      p => p.status?.toLowerCase() === "completed",
                    ).length
                  }
                </div>
                <div className="text-gray-600">Completed</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-blue-600 mb-2">
                  {
                    (searchTerm ? filteredProjects : projects).filter(
                      p => p.status?.toLowerCase() === "inprogress",
                    ).length
                  }
                </div>
                <div className="text-gray-600">In Progress</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-yellow-600 mb-2">
                  {
                    (searchTerm ? filteredProjects : projects).filter(
                      p => p.status?.toLowerCase() === "planning",
                    ).length
                  }
                </div>
                <div className="text-gray-600">Planning</div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Project Information Modal */}
      <ProjectInformationModal
        open={modalOpen}
        onClose={handleCloseModal}
        project={selectedProject}
      />
    </div>
  );
}
